/**
 * Single source of truth for site-wide SEO metadata.
 *
 * This file is imported by BOTH the React app (src/components/Seo.jsx) and the
 * Node build script (scripts/prerender-seo.mjs), so it must stay dependency-free
 * and use only plain ESM syntax. Do not add imports here.
 */

export const SITE_URL = "https://www.darkdefensive.com";
export const SITE_NAME = "Dark Defensive";
export const LEGAL_NAME = "Dark Defensive LLC";
export const LOCALE = "en_US";
export const CONTACT_EMAIL = "info@darkdefensive.com";

export const DEFAULT_TITLE =
  "Dark Defensive | Automate Compliance. Reduce Cyber Risk.";

export const DEFAULT_DESCRIPTION =
  "Dark Defensive is an AI-powered cyber GRC platform that automates controls, evidence, risk, and audit readiness for lean security and compliance teams.";

/** 1200x630 social preview card. Lives in /public so the URL is stable. */
export const DEFAULT_OG_IMAGE = "/og/dark-defensive-og.png";
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const DEFAULT_OG_IMAGE_ALT =
  "Dark Defensive — AI-powered cyber GRC platform. Automate compliance. Reduce cyber risk.";

/** Stable, unhashed logo URL for structured data. */
export const LOGO_PATH = "/dd-logo.png";
export const LOGO_WIDTH = 1400;
export const LOGO_HEIGHT = 245;

export const SOCIAL_PROFILES = [
  "https://www.linkedin.com/company/dark-defensive/",
  "https://www.youtube.com/@DarkDefensive",
  "https://www.reddit.com/user/darkDefensive",
];

/**
 * Per-route metadata for every statically-routed page.
 * Keys must match the paths declared in src/App.js.
 */
export const ROUTE_META = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    type: "website",
    changefreq: "weekly",
    priority: "1.0",
  },
  "/about": {
    title: "About Dark Defensive",
    description:
      "Built by practitioners with decades of enterprise security, GRC, and incident response experience — meet the team behind the Dark Defensive platform.",
    type: "website",
    changefreq: "monthly",
    priority: "0.8",
  },
  "/contact-us": {
    title: "Contact Us & Request a Demo",
    description:
      "Request a demo or talk to the Dark Defensive team about compliance automation, risk quantification, dark web monitoring, and continuous audit readiness.",
    type: "website",
    changefreq: "monthly",
    priority: "0.9",
  },
  "/blog": {
    title: "Cyber GRC & Risk Blog",
    description:
      "Insights on cyber risk management, GRC automation, dark web threat intelligence, and compliance frameworks from the Dark Defensive team.",
    type: "website",
    changefreq: "weekly",
    priority: "0.7",
  },
  "/case-studies": {
    title: "Case Studies",
    description:
      "See how organizations use Dark Defensive to assess ransomware risk, reduce security technical debt, protect data, and build cyber resilience.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description:
      "How Dark Defensive collects, uses, stores, and protects personal information, and the privacy choices and rights available to you.",
    type: "website",
    changefreq: "yearly",
    priority: "0.3",
  },
  "/accessibility": {
    title: "Accessibility",
    description:
      "Dark Defensive aims to meet WCAG 2.1 Level AA. Read what we have built in, our known limitations, and how to report an accessibility barrier.",
    type: "website",
    changefreq: "yearly",
    priority: "0.3",
  },
};

/** Static paths that CMS slugs must never shadow (avoids duplicate content). */
export const RESERVED_PATHS = Object.keys(ROUTE_META);

/**
 * CMS `Page` slugs that duplicate a hand-built React route. These are reachable
 * through the /:slug catch-all but must stay out of the sitemap and prerender.
 */
export const EXCLUDED_CMS_PAGE_SLUGS = ["about-us", "privacy-policy"];

/** Build an absolute URL from a site-relative path. */
export function absoluteUrl(path = "/") {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * `https://<region>.graphassets.com/<environment>/[transforms/]<handle>`
 * Only the environment prefix and the trailing handle matter; any transforms
 * already in the URL are dropped and replaced with ours.
 */
const HYGRAPH_ASSET_RE =
  /^(https:\/\/[^/]*graphassets\.com\/[A-Za-z0-9]+)\/(?:.*\/)?([A-Za-z0-9]+)\/?$/;

/**
 * Resolve the image a social crawler should be handed.
 *
 * Hygraph serves its originals as AVIF via content negotiation, and the source
 * files are often portrait and smaller than the 1200x627 LinkedIn wants — both
 * of which make the preview image silently disappear. Hygraph's transform API
 * fixes both: force JPEG and crop to a 1200x630 landscape card. Non-Hygraph
 * URLs are passed through with their dimensions left undeclared, since claiming
 * the wrong ones also breaks the preview.
 */
export function resolveSocialImage(image) {
  if (!image) {
    return {
      url: absoluteUrl(DEFAULT_OG_IMAGE),
      type: "image/png",
      width: OG_IMAGE_WIDTH,
      height: OG_IMAGE_HEIGHT,
    };
  }
  const match = String(image).match(HYGRAPH_ASSET_RE);
  if (match) {
    const [, envBase, handle] = match;
    return {
      url: `${envBase}/output=format:jpg/resize=width:${OG_IMAGE_WIDTH},height:${OG_IMAGE_HEIGHT},fit:crop/${handle}`,
      type: "image/jpeg",
      width: OG_IMAGE_WIDTH,
      height: OG_IMAGE_HEIGHT,
    };
  }
  return { url: absoluteUrl(image), type: null, width: null, height: null };
}

/** Look up route metadata, falling back to site defaults. */
export function getRouteMeta(path) {
  return ROUTE_META[path] || null;
}

/**
 * Filler copy that must never be published as a meta description. Checked
 * against the opening of the text, since lorem ipsum boilerplate often starts
 * with a sentence of English ("Contrary to popular belief, Lorem Ipsum is…").
 */
const PLACEHOLDER_RE = /lorem ipsum|dolor sit amet/i;
const PLACEHOLDER_SCAN_CHARS = 300;

/**
 * Collapse whitespace and truncate at a word boundary for meta descriptions.
 * Returns `fallback` when the source is empty or is unreplaced filler copy.
 */
export function toDescription(text, { max = 160, fallback = DEFAULT_DESCRIPTION } = {}) {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (!clean || PLACEHOLDER_RE.test(clean.slice(0, PLACEHOLDER_SCAN_CHARS))) {
    return fallback;
  }
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:.\-–—]$/, "")}…`;
}

/** Organization node, referenced by @id from every other structured-data node. */
export const organizationSchema = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: LEGAL_NAME,
  url: `${SITE_URL}/`,
  email: CONTACT_EMAIL,
  description: DEFAULT_DESCRIPTION,
  logo: {
    "@type": "ImageObject",
    "@id": `${SITE_URL}/#logo`,
    url: absoluteUrl(LOGO_PATH),
    width: LOGO_WIDTH,
    height: LOGO_HEIGHT,
    caption: SITE_NAME,
  },
  image: { "@id": `${SITE_URL}/#logo` },
  sameAs: SOCIAL_PROFILES,
};

export const websiteSchema = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: SITE_NAME,
  description: DEFAULT_DESCRIPTION,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-US",
};

/** Product schema for the home page. */
export const softwareApplicationSchema = {
  "@type": "SoftwareApplication",
  "@id": `${SITE_URL}/#platform`,
  name: "Dark Defensive Cyber GRC Platform",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Governance, Risk and Compliance (GRC)",
  operatingSystem: "Web-based (SaaS)",
  url: `${SITE_URL}/`,
  description:
    "AI-powered SaaS platform for control management, evidence automation, risk quantification, dark web threat intelligence, and continuous audit readiness.",
  publisher: { "@id": `${SITE_URL}/#organization` },
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceSpecification: {
      "@type": "PriceSpecification",
      priceCurrency: "USD",
    },
  },
  featureList: [
    "AI GRC Assistant",
    "Control Management",
    "Evidence Automation",
    "Asset and Control Mapping",
    "Risk Register",
    "Executive Dashboards",
    "Dark Web Threat Intelligence",
    "FAIR Risk Quantification",
  ],
};

/** The site-wide graph injected on every page. */
export const baseSchemaGraph = [organizationSchema, websiteSchema];

/** BreadcrumbList builder. `crumbs` is [{ name, path }] ordered root-first. */
export function breadcrumbSchema(crumbs) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}
